import { useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { useIdleTimer } from "react-idle-timer";
import useMixpanelEventAction from "../config/mixpanel/useMixpanelEventAction";
import { useDispatch, useSelector } from "react-redux";
import { userLogout } from "../redux/loginSlice";
import { idealTimeOutContent } from "../locales/en";
import {
  STORAGE_LOGOUT_KEY,
  STORAGE_RESET_KEY
} from "../utils/constants/appConstants";

const useIdleTimeout = (timeoutDuration = 1000 * 60 * 15) => {
  const { authUser, authUserLoading } = useSelector((state) => state.auth);

  const { logoutMixpanelEvent } = useMixpanelEventAction();
  const dispatch = useDispatch();

  const performLogout = useCallback(() => {
    logoutMixpanelEvent();
    dispatch(userLogout());
  }, [dispatch, logoutMixpanelEvent]);

  const { pause, resume, reset, isLeader } = useIdleTimer({
    timeout: timeoutDuration,
    onIdle: () => {
      if (isLeader()) {
        handleIdle();
      }
    },
    debounce: 500,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200
  });

  const handleIdle = useCallback(() => {
    let timerInterval;
    Swal.fire({
      title: idealTimeOutContent?.title,
      html: idealTimeOutContent?.content,
      timer: 30000,
      showCancelButton: true,
      showConfirmButton: true,
      cancelButtonColor: "#dc3545",
      confirmButtonColor: "#14bc96",
      confirmButtonText: idealTimeOutContent?.moreTime,
      cancelButtonText: idealTimeOutContent?.logout,
      allowOutsideClick: false,
      didOpen: () => {
        const timer = Swal.getPopup()?.querySelector("b");
        timerInterval = setInterval(() => {
          if (timer) {
            timer.textContent = `${Math.ceil(Swal.getTimerLeft() / 1000)}`;
          }
        }, 1000);
      },
      willClose: () => {
        clearInterval(timerInterval);
      }
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.setItem(STORAGE_RESET_KEY, Date.now().toString());

        Swal.close();
        reset();

        Swal.fire({
          title: "Stay connected!",
          color: "#14bc96",
          icon: "success",
          iconColor: "#14bc96",
          showConfirmButton: false,
          timer: 2000
        });
      } else if (
        result.dismiss === Swal.DismissReason.timer ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        localStorage.setItem(STORAGE_LOGOUT_KEY, Date.now().toString());

        performLogout();
      }
    });
  }, [performLogout, reset]);

  /**
   * Listen for cross-tab reset/logout events
   */
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === STORAGE_RESET_KEY) {
        Swal.close();
        reset();
      }

      if (event.key === STORAGE_LOGOUT_KEY) {
        Swal.close();
        performLogout();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [performLogout, reset]);

  /**
   * Enable/disable idle timer based on auth state
   */
  useEffect(() => {
    if (authUser && !authUserLoading) {
      resume();
    } else {
      pause();
    }
  }, [authUser, authUserLoading, pause, resume]);

  return {};
};

export default useIdleTimeout;
